import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Grid, TextField, Typography } from '@mui/material'

import BaseDialog from '../BaseDialog'
import { useDataApi } from '../../context/DataApiContext'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import {
  getPVAdmissionErrorMessages,
  getPVAdmissionDocumentWarnings,
  getPVAdmissionWarningMessages,
} from '../../utils/pvAdmissionTemplateErrors'

const KEY = 'pv-admission-management'

export default function CreateTemplateDialog({
  open,
  onClose = () => {},
  onCreated = () => {},
}) {
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [name, setName] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState([])
  // Warnings of the document sent last; confirming again saves it anyway
  const [warnings, setWarnings] = useState(null)
  // Template created by a confirmation whose document was refused: the next
  // confirmation reuses it, and cancelling deletes it.
  const [pendingTemplate, setPendingTemplate] = useState(null)

  const reset = () => {
    setName('')
    setFile(null)
    setLoading(false)
    setErrors([])
    setWarnings(null)
    setPendingTemplate(null)
  }

  const close = () => {
    if (pendingTemplate) {
      dataProvider.pvAdmissionTemplates
        .remove(pendingTemplate.id)
        .catch(() => showNotification('error', t(`${KEY}.errors.generic`)))
    }
    reset()
    onClose()
  }

  // Creates the template, or renames the pending one when the name was changed
  const saveTemplate = () => {
    const trimmedName = name.trim()
    if (!pendingTemplate) {
      return dataProvider.pvAdmissionTemplates.create(trimmedName)
    }
    if (pendingTemplate.name !== trimmedName) {
      return dataProvider.pvAdmissionTemplates.rename(
        pendingTemplate.id,
        trimmedName
      )
    }
    return Promise.resolve(pendingTemplate)
  }

  const onConfirm = async () => {
    setLoading(true)
    setErrors([])

    let template
    try {
      template = await saveTemplate()
      setPendingTemplate(template)
    } catch (err) {
      setErrors(getPVAdmissionErrorMessages(err, t))
      setLoading(false)
      return
    }

    try {
      if (warnings) {
        await dataProvider.pvAdmissionTemplates.acceptDocumentWarnings(
          template.id,
          warnings.stagingKey
        )
      } else {
        await dataProvider.pvAdmissionTemplates.uploadDocument(
          template.id,
          file
        )
      }
    } catch (err) {
      // The dialog stays open to choose another document, save this one
      // despite its warnings, or cancel
      const documentWarnings = getPVAdmissionDocumentWarnings(err)
      if (documentWarnings) {
        setWarnings(documentWarnings)
      } else {
        setWarnings(null)
        setErrors(getPVAdmissionErrorMessages(err, t))
      }
      setLoading(false)
      return
    }

    showNotification(
      'success',
      t(`${KEY}.create-dialog.success`, { name: template.name })
    )
    reset()
    onCreated(template)
  }

  return (
    <BaseDialog
      open={!!open}
      onClose={close}
      onConfirm={onConfirm}
      title={t(`${KEY}.create-dialog.title`)}
      loading={loading}
      openButtonTitle={warnings ? t(`${KEY}.warnings.save-anyway`) : undefined}
      confirmDisabled={name.trim().length === 0 || !file}
    >
      <Grid container direction="column" spacing={3} sx={{ pt: 2 }}>
        <Grid item>
          <TextField
            variant="standard"
            required
            id="pv-admission-template-name"
            label={t(`${KEY}.create-dialog.name`)}
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Grid>
        <Grid item>
          <Button variant="outlined" component="label">
            {warnings
              ? t(`${KEY}.warnings.choose-another-file`)
              : t(`${KEY}.create-dialog.document`)}
            <input
              hidden
              type="file"
              data-testid="pv-admission-template-file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(event) => {
                setFile(event.target.files[0] || null)
                setWarnings(null)
                setErrors([])
                // Lets the same file be chosen again once corrected
                event.target.value = ''
              }}
            />
          </Button>
          {file && <Typography variant="body2">{file.name}</Typography>}
        </Grid>
        {warnings && (
          <Grid item data-testid="pv-admission-template-warnings">
            {getPVAdmissionWarningMessages(warnings, t).map((message) => (
              <Typography key={message} color="warning.main" variant="body2">
                {message}
              </Typography>
            ))}
          </Grid>
        )}
        {errors.length > 0 && (
          <Grid item data-testid="pv-admission-template-errors">
            {errors.map((message) => (
              <Typography key={message} color="error" variant="body2">
                {message}
              </Typography>
            ))}
          </Grid>
        )}
      </Grid>
    </BaseDialog>
  )
}
